import axios from "axios";
import classnames from "classnames";
import settings from "client.settings.json";
import { useAutoSave } from "hooks/useAutoSave";
import mime from "mime-types";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FiSend } from "react-icons/fi";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { connect } from "react-redux";
import { ToastsStore } from "react-toasts";
import { translate } from "reducers/i18n";
import Attachment from "./attachment";
import styles from "./compose.module.css";
import highRiskTypes from "./highRiskTypes";
import SubmissionPage from "./submissionPage";

const { CancelToken } = axios;

const createObjectId = () => {
  const timestamp = ((Date.now() / 1000) | 0).toString(16);
  const randomBytes = Array.from(window.crypto.getRandomValues(new Uint8Array(8)));
  const randomHex = randomBytes.map(byte => `0${byte.toString(16)}`.slice(-2)).join("");
  return `${timestamp}${randomHex}`;
};

const formatSizeString = bytes => {
  const sizeMb = (bytes / (1024 * 1024)).toFixed(2);
  return sizeMb.replace(/\.?0+$/, ""); // Remove trailing decimals if redundant.
};

const linkify = str => {
  const urlPattern = /(\b(http:\/\/|https:\/\/|www\.)[^\s<>"'(),!?]+)(?=\s|$|[,!?<br/?>])/gi;

  const parts = str.split(/(<a\s+[^>]*>.*?<\/a>)/gi);
  for (let i = 0; i < parts.length; i++) {
    if (!parts[i].startsWith("<a")) {
      parts[i] = parts[i].replace(urlPattern, match => {
        let url = match;
        if (!url.startsWith("http")) {
          url = "http://" + url;
        }
        return `<a href="${url}">${match}</a>`;
      });
    }
  }

  return parts.join("");
};

const Compose = ({
  attachmentExtentions,
  attachmentsAllowed,
  body,
  message,
  messageId,
  maxMailSize,
  setShowReply,
  tenantId,
  replyAll
}) => {
  const [transactionId, setTransactionId] = useState("");
  const [haystackMessageId, setHaystackMessageId] = useState("");
  const messageIdPromiseRef = useRef(null);
  const textAreaRef = useRef();
  const { createdAt, headers } = message;
  const parsedSubject = headers.find(({ name }) => name === "subject")?.value || "";
  const from = message.sender.alias ? `${message.sender.alias} &lt;${message.sender.email}&gt;` : message.sender.email;

  const to = message.recipient.alias
    ? `${message.recipient.alias} &lt;${message.recipient.email}&gt;`
    : message.recipient.email;

  const subjectWithPrefix = `RE: ${parsedSubject}`;
  let senderAlias = message?.recipient?.alias || message?.recipient?.email || "";
  if (senderAlias.includes("@")) senderAlias = senderAlias.split("@")?.[0] || "";

  const bodyWithPrefix = `
<br><br>
-----${translate("compose_originalMessage")}-----<br>
<strong>${translate("_from")}:</strong> ${from}<br>
<strong>${translate("compose_dateSent")}:</strong> ${new Date(
    createdAt
  ).toLocaleDateString()} ${new Date(createdAt).toLocaleTimeString()}<br>
<strong>${translate("compose_to")}:</strong> ${to}<br>
<strong>${translate("_subject")}:</strong> ${parsedSubject}<br>
${body.replace(/<img[^>]*>/g, "")}`;

  useEffect(() => {
    if (textAreaRef.current) textAreaRef.current.focus();
  }, []);

  const cancelTokens = useRef({});
  const [attachments, setAttachments] = useState([]);
  const [composeMessageId, setComposeMessageId] = useState("");
  const [currentlyUploading, setCurrentlyUploading] = useState({});
  const [errors, setErrors] = useState({});
  const [handleSaveError, setHandleSaveError] = useState("");
  const [submissionError, setSubmissionError] = useState("");
  const [html, setHtml] = useState(bodyWithPrefix);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [sent, setSent] = useState(false);
  const [tokens, setTokens] = useState({ zip: "", pdf: "" });
  const [values, setValues] = useState({ subject: subjectWithPrefix, senderAlias: senderAlias });
  const apiStem = `${settings.api}/api/messages/${messageId}/reply`;
  const isStillUploading = Object.values(currentlyUploading).some(Boolean);
  const attachmentIdString = attachments.map(({ _id }) => _id).join(",");
  const currentlyUploadingIdString = Object.keys(currentlyUploading).filter(Boolean).join(",");

  const totalSizeBytes = useMemo(
    () => Math.ceil(attachments.reduce((sum, f) => sum + f.size, 0)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [attachmentIdString]
  );

  const [attachmentTotalSize, displayUnit] = useMemo(() => {
    if (totalSizeBytes >= 1024 * 1024) return [(totalSizeBytes / (1024 * 1024)).toFixed(2), "MB"];
    return [(totalSizeBytes / 1024).toFixed(0), "KB"];
  }, [totalSizeBytes]);

  const getMessageId = useCallback(async () => {
    try {
      if (messageIdPromiseRef.current) {
        const res = await messageIdPromiseRef.current;
        return res.data._id;
      }

      if (composeMessageId) return composeMessageId;
      let url = `${apiStem}`;
      if (replyAll) url += "/all";
      const options = { headers: { tenant: tenantId } };
      messageIdPromiseRef.current = axios.get(url, options);
      const res = await messageIdPromiseRef.current;
      setComposeMessageId(res.data._id);
      return res.data._id;
    } catch (error) {
      console.error(error);
    } finally {
      messageIdPromiseRef.current = null;
    }
  }, [composeMessageId, apiStem, replyAll, tenantId]);

  const handleChange = e => {
    const { name, value } = e.target;
    setErrors(({ [name]: fieldName, ...rest }) => ({ ...rest }));
    setValues(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = useCallback(async () => {
    try {
      setHandleSaveError("");
      const composeMessageId = await getMessageId();
      const { subject, senderAlias } = values;
      const htmlReplaced = html.replace(/<p>|<br>/gi, "").replace(/<\/p>/gi, "<br/>");
      const htmlFormatted = linkify(htmlReplaced);
      const formData = new FormData();
      formData.append("messageId", composeMessageId);
      if (subject) formData.append("subject", subject);
      if (senderAlias) formData.append("senderAlias", senderAlias);
      if (html) formData.append("html", htmlFormatted);
      if (!Array.from(formData.entries()).length) return;
      await axios.post(`${apiStem}`, formData, {
        headers: { tenant: tenantId },
      });
      return composeMessageId;
    } catch (error) {
      console.error(error);
      setHandleSaveError("compose_handleSaveError");
    }
  }, [apiStem, getMessageId, html, values]);

  const { handleBlur, handleFocus } = useAutoSave({ html, ...values }, handleSave, {
    canSave: !isSubmitting,
    resetDependency: composeMessageId
  });

  const validate = useCallback(() => {
    const errors = {};

    if (totalSizeBytes > maxMailSize) {
      errors.attachments = translate("compose_attachmentSize", formatSizeString(maxMailSize));
    }

    return errors;
  }, [totalSizeBytes, maxMailSize]);

  const handleSubmission = useCallback(async () => {
    const errors = validate();
    setErrors(errors);
    if (Object.values(errors).some(Boolean)) return;

    if (isStillUploading) {
      setHandleSaveError("compose_pleaseWaitMessage");
      return;
    }

    try {
      setIsSubmitting(true);
      const savedMessageId = await handleSave();
      const messageId = savedMessageId || composeMessageId;

      if (!messageId) {
        setHandleSaveError("compose_handleSaveError");
        return;
      }

      const url = `${apiStem}/${messageId}/send`;
      const options = { headers: { tenant: tenantId } };
      const res = await axios.get(url, options);
      setComposeMessageId("");
      setUploadProgress({});
      setValues({});
      setHtml("");
      const { haystackMessageId, transactionId, replyPdfToken, replyZipToken } = res.data;
      setHaystackMessageId(haystackMessageId);
      setTokens({ zip: replyZipToken, pdf: replyPdfToken });
      setTransactionId(transactionId);
      ToastsStore.success(translate("compose_sent_success"));
      setSent(true);
    } catch (error) {
      console.error(error);
      setSubmissionError("compose_submissionError");
      setSent(true);
    } finally {
      setIsSubmitting(false);
    }
  }, [apiStem, composeMessageId, handleSave, isStillUploading, tenantId, validate]);

  const handleDeleteAttachment = useCallback(
    async attachmentId => {
      const attachmentToDelete = attachments.find(({ _id }) => _id === attachmentId);
      setAttachments(prev => prev.filter(attachment => attachment._id !== attachmentId));
      setErrors(({ attachments, ...prev }) => prev);

      try {
        if (currentlyUploading[attachmentId]) {
          cancelTokens.current[attachmentId]?.cancel();
        }

        if (composeMessageId) {
          const url = `${apiStem}/${composeMessageId}/attachment/${attachmentId}`;
          await axios.delete(url, { headers: { tenant: tenantId } });
        }

        setUploadProgress(({ [attachmentId]: _, ...rest }) => rest);
        // eslint-disable-next-line no-unused-vars
      } catch (error) {
        setAttachments(prev => [...prev, attachmentToDelete]);
        setErrors(prev => ({ ...prev, attachments: translate("compose_deleteAttachmentError") }));
      } finally {
        setCurrentlyUploading(({ [attachmentId]: _, ...rest }) => rest);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [apiStem, attachmentIdString, composeMessageId, currentlyUploadingIdString, tenantId]
  );

  const handleUploads = useCallback(
    async files => {
      const BATCH_SIZE = 5;
      const batchSizeBytes = Math.ceil(files.reduce((sum, f) => sum + f.size, 0));

      if (totalSizeBytes + batchSizeBytes > maxMailSize) {
        setErrors(prev => ({
          ...prev,
          attachments: translate("compose_attachmentSize", formatSizeString(maxMailSize))
        }));

        return;
      }

      const composeMessageId = await getMessageId();
      const filesWithIds = files.map(file => ({ _id: createObjectId(), file, filename: file.name, size: file.size }));
      setAttachments(prev => [...prev, ...filesWithIds]);
      setCurrentlyUploading(prev => filesWithIds.reduce((prev, file) => ({ ...prev, [file._id]: true }), prev));
      setErrors(prev => ({ ...prev, attachments: [] })); // Clear attachment errors.

      const uploadSingleFile = async ({ _id: attachmentId, filename, file }) => {
        const onUploadProgress = e => {
          const decimalComplete = e.loaded / e.total;
          const uploadProgress = (decimalComplete * 100).toFixed(1);
          setUploadProgress(prev => ({ ...prev, [attachmentId]: uploadProgress }));
          setCurrentlyUploading(prev => ({ ...prev, [attachmentId]: true }));
        };

        const url = `${apiStem}/${composeMessageId}/attachment/${attachmentId}`;
        const formData = new FormData();
        formData.append(filename, file);
        cancelTokens.current[attachmentId] = CancelToken.source();
        setCurrentlyUploading(prev => ({ ...prev, [attachmentId]: true }));

        const config = {
          cancelToken: cancelTokens.current[attachmentId].token,
          headers: { tenant: tenantId },
          onUploadProgress
        };

        try {
          const { data: uploaded } = await axios.put(url, formData, config);
          setAttachments(prev => prev.map(attachment => (attachment._id === attachmentId ? uploaded : attachment)));
          setUploadProgress(({ [attachmentId]: progress, ...rest }) => ({ ...rest, [uploaded._id]: progress }));
        } catch (error) {
          if (axios.isCancel(error)) return;
          throw error;
        } finally {
          setCurrentlyUploading(prev => ({ ...prev, [attachmentId]: false }));
        }
      };

      const uploadResults = await filesWithIds
        .flatMap((_, index, files) => (index % BATCH_SIZE ? [] : [files.slice(index, index + BATCH_SIZE)]))
        .reduce(async (prevBatch, currentBatch) => {
          const prevResults = await prevBatch;
          const uploads = currentBatch.map(uploadSingleFile);
          const uploadResults = await Promise.allSettled(uploads);
          return [...prevResults, ...uploadResults];
        }, Promise.resolve([]));

      const uploadErrors = uploadResults.reduce((errors, res, index) => {
        if (res.status === "rejected") {
          const file = filesWithIds[index];
          setUploadProgress(({ [file._id]: fileId, ...prev }) => prev);
          setAttachments(prev => prev.filter(prevFile => prevFile._id !== file._id));
          return [...errors, { filename: file.filename }];
        }

        return errors;
      }, []);

      if (uploadErrors.length) {
        setErrors(prev => ({
          ...prev,
          attachments: `${translate("compose_uploadError")} ${uploadErrors
            .map(file => file.filename)
            .join(", ")}. ${translate("compose_uploadHint", formatSizeString(maxMailSize))}`
        }));
      }
    },
    [apiStem, totalSizeBytes, getMessageId, maxMailSize, tenantId]
  );

  const handleRejected = useCallback(rejectedList => {
    setErrors(prev => ({
      ...prev,
      attachments: rejectedList
        .map(({ file, errors }) => {
          const errorMessages = errors.map(e => e.message).join("<br />");
          return `<div class="text-left mb-2"><strong>${file.name}</strong>:<br />${errorMessages}</div>`;
        })
        .join("\n")
    }));
  }, []);

  const accept = attachmentExtentions.reduce(
    (prev, ext) => ({
      ...prev,
      [mime.lookup(ext)]: [...(prev[mime.lookup(ext)] || []), `.${ext}`]
    }),
    {}
  );

  const fileTypeValidator = useCallback(data => {
    const { type } = data;
    const ext = mime.extension(type) || data.name?.split(".").pop();

    if (ext && highRiskTypes.includes(ext)) {
      return {
        code: "typeNotAllowed",
        message: translate("compose_typeNotAllowed", type || ext, ext)
      };
    }

    return null;
  }, []);

  const { getInputProps, getRootProps, isDragActive, isDragReject } = useDropzone({
    accept,
    maxSize: maxMailSize,
    onDropAccepted: handleUploads,
    onDropRejected: handleRejected,
    noClick: false,
    noKeyboard: true,
    validator: fileTypeValidator
  });

  const { ref, ...rootProps } = getRootProps();

  return (
    <>
      <div className="panel mb-3">
        {!sent ? (
          <>
            <div className="panelHeader header_fontFamily">
              <h1>{translate("message_reply")}</h1>
            </div>
            <div className="panelContent">
              <div className="panelProps">
                {handleSaveError && (
                  <div className="row panelPropRow">
                    <div className="error py-1 px-3" style={{ marginTop: "-.5rem" }}>
                      {translate(handleSaveError)}
                    </div>
                  </div>
                )}
                <div className="row panelPropRow">
                  <div className="col-sm-2">{translate("To")}:</div>
                  <div className="col-sm-10">
                    {message.headers
                      .filter(e => ["from", ...(replyAll ? ["to"] : [])].includes(e.name))
                      .reduce((acc, header) => acc.concat(header.addresses), [])
                      .filter(e => e.address !== message.recipient.email)
                      .map(e => `${e.displayName} (${e.address})`)
                      .join(", ")}
                  </div>
                </div>
                {replyAll ? (
                  <>
                    <div className="row panelPropRow">
                      <div className="col-sm-2">{translate("Cc")}:</div>
                      <div className="col-sm-10">
                        {message.headers
                          .filter(e => ["cc"].includes(e.name))
                          .reduce((acc, header) => acc.concat(header.addresses), [])
                          .filter(e => e.address !== message.recipient.email)
                          .map(e => `${e.displayName} (${e.address})`)
                          .join(", ")}
                      </div>
                    </div>
                  </>
                ) : (
                  ""
                )}
                <div className="row panelPropRow">
                  <div className="col-sm-2">{translate("_subject")}:</div>
                  <div className="col-sm-10">
                    <input
                      className={styles.subject}
                      id="subject"
                      name="subject"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      placeholder=""
                      value={values.subject ?? ""}
                    />
                  </div>
                </div>
                <div className="row panelPropRow">
                  <div className="col-sm-2">{translate("_yourname")}:</div>
                  <div className="col-sm-10">
                    <input
                      className={styles.senderAlias}
                      id="senderAlias"
                      name="senderAlias"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      onFocus={handleFocus}
                      placeholder=""
                      value={values.senderAlias ?? ""}
                    />
                  </div>
                </div>
                <div className="row panelPropRow">
                  <div className="col-sm-2">{translate("compose_message")}</div>
                  <div
                    className="col-sm-10"
                    id="quill-body-container"
                    onFocus={handleFocus}
                    onBlur={e => {
                      // Use wrapper for blur to avoid deferred Quill blur handler.
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        handleBlur();
                      }
                    }}
                  >
                    <ReactQuill
                      className={styles.input}
                      id="html"
                      modules={{ toolbar: [["bold", "italic", "underline"]] }}
                      name="html"
                      placeholder=""
                      ref={el => (textAreaRef.current = el)}
                      theme="snow"
                      value={html ?? ""}
                      onChange={setHtml}
                    />
                  </div>
                </div>
                {attachmentsAllowed ? (
                  <div className="row panelPropRow">
                    <div className="col-sm-2">{translate("compose_attachments")}:</div>
                    <div className="col-sm-10">
                      <div
                        className={classnames(styles.dropzone, {
                          [styles.active]: isDragActive,
                          [styles.rejected]: isDragReject
                        })}
                        {...rootProps}
                      >
                        <input {...getInputProps()} />
                        {translate("compose_dragAndDrop")}
                      </div>
                      <p>
                        {translate("compose_accepted")}: {attachmentExtentions.map(f => `.${f}`).join(", ")}.
                        <br />
                        {translate("compose_maxMailSize", formatSizeString(maxMailSize))}
                        .
                        <br />
                        <span
                          className={classnames({
                            [styles.sizeLimitExceeded]: totalSizeBytes > maxMailSize
                          })}
                        >
                          {translate("compose_currentTotalSize", attachmentTotalSize, displayUnit)}
                        </span>
                      </p>
                      {errors.attachments ? (
                        <div
                          className="error my-3 py-1 px-0"
                          dangerouslySetInnerHTML={{ __html: errors.attachments }}
                        />
                      ) : null}
                      {attachments.map(attachment => {
                        const { _id: attachmentId } = attachment;

                        return (
                          <Attachment
                            key={attachmentId}
                            attachment={attachment}
                            handleDeleteAttachment={() => handleDeleteAttachment(attachmentId)}
                            hasError={false}
                            isUploadingAttachment={currentlyUploading[attachmentId]}
                            uploadProgress={uploadProgress[attachmentId]}
                          />
                        );
                      })}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            <div className="panelFooter buttons_fontFamily">
              <div className="messageFooter ">
                <button tabIndex="0" className="button" disabled={isSubmitting} onClick={handleSubmission}>
                  <FiSend className="mr-2" />
                  {translate("_send")}
                </button>
                <button tabIndex="0" className="btn btn-secondary text-dark" onClick={() => setShowReply(false)}>
                  {translate("_cancel")}
                </button>
              </div>
            </div>
          </>
        ) : submissionError ? (
          <div className="panel mt-0 border-0">
            <div className="row panelPropRow bg-white" style={{ padding: "20px 15px" }}>
              <div className="error">{translate(submissionError)}</div>
            </div>
            <div className="panelFooter buttons_fontFamily">
              <div className="messageFooter ">
                <button tabIndex="0" className="btn btn-secondary text-dark" onClick={() => setShowReply(false)}>
                  {translate("compose_BackToMessage")}
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="panel mt-0 border-0">
            <SubmissionPage
              tenantId={tenantId}
              name={""}
              attachments={attachments}
              transactionId={transactionId}
              messageId={haystackMessageId}
              tokens={tokens}
            />
            <div className="panelFooter buttons_fontFamily">
              <div className="messageFooter ">
                <button tabIndex="0" className="btn btn-secondary text-dark" onClick={() => setShowReply(false)}>
                  {translate("compose_BackToMessage")}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

Compose.propTypes = {
  attachmentExtentions: PropTypes.arrayOf(PropTypes.string).isRequired,
  attachmentsAllowed: PropTypes.bool.isRequired,
  body: PropTypes.string.isRequired,
  message: PropTypes.shape({
    sender: PropTypes.shape({
      alias: PropTypes.string,
      email: PropTypes.string.isRequired
    }).isRequired,
    recipient: PropTypes.shape({
      alias: PropTypes.string,
      email: PropTypes.string.isRequired
    }).isRequired,
    headers: PropTypes.array,
    createdAt: PropTypes.string.isRequired
  }).isRequired,
  messageId: PropTypes.string.isRequired,
  maxMailSize: PropTypes.number.isRequired,
  setShowReply: PropTypes.func.isRequired,
  tenantId: PropTypes.string.isRequired,
  replyAll: PropTypes.bool.isRequired,
  replyAlwaysEft: PropTypes.bool.isRequired
};

const mapStateToProps = state => state.i18n;
export default connect(mapStateToProps)(Compose);
